import { useQueryClient } from "@tanstack/react-query";
import { CheckIcon, CopyIcon, Loader2, Trash2Icon } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getGetCaseGuestsQueryKey,
  useGetCaseGuests,
  useRevokeGuestAccess,
  useShareCase,
} from "~/api/users/users";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { useCopyContent } from "~/hooks/useCopyContent";
import { trackEvent } from "~/utils/analytics";

interface ShareCaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  caseId: string;
  chatId: string;
  caseName?: string;
}

export const ShareCaseDialog: FC<ShareCaseDialogProps> = ({
  open,
  onOpenChange,
  organizationId,
  caseId,
  chatId,
  caseName,
}) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { handleCopy } = useCopyContent();

  // Fetch existing guests
  const { data: guests } = useGetCaseGuests(organizationId, caseId, caseId, {
    query: {
      enabled: open,
    },
  });

  const { mutate: shareCase, isPending } = useShareCase({
    mutation: {
      onSuccess: () => {
        trackEvent({ eventName: "app/doc_list/share_case/success" });
        setEmail("");
        setError(null);
        // Refresh the guests list
        queryClient.invalidateQueries({
          queryKey: getGetCaseGuestsQueryKey(organizationId, caseId, caseId),
        });
      },
      onError: () => {
        trackEvent({ eventName: "app/doc_list/share_case/error" });
        setError(
          intl.formatMessage({
            id: "share.error.generic",
            defaultMessage: "Failed to create share link. Please try again.",
          })
        );
      },
    },
  });

  const { mutate: revokeAccess, isPending: isRevoking } = useRevokeGuestAccess({
    mutation: {
      onSuccess: () => {
        trackEvent({ eventName: "app/doc_list/share_case/revoke_success" });
        // Refresh the guests list
        queryClient.invalidateQueries({
          queryKey: getGetCaseGuestsQueryKey(organizationId, caseId, caseId),
        });
      },
      onError: () => {
        trackEvent({ eventName: "app/doc_list/share_case/revoke_error" });
      },
    },
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = () => {
    if (!email.trim()) {
      setError(
        intl.formatMessage({
          id: "share.error.emailRequired",
          defaultMessage: "Email is required",
        })
      );
      return;
    }

    if (!validateEmail(email)) {
      setError(
        intl.formatMessage({
          id: "share.error.invalidEmail",
          defaultMessage: "Please enter a valid email address",
        })
      );
      return;
    }

    trackEvent({ eventName: "app/doc_list/share_case/submit" });
    shareCase({
      organizationId,
      projectId: caseId,
      data: {
        caseId,
        chatId,
        email,
      },
    });
  };

  const handleClose = () => {
    setEmail("");
    setError(null);
    setCopiedId(null);
    onOpenChange(false);
  };

  const handleCopyLink = (guestId: string, shareUrl: string) => {
    handleCopy(shareUrl);
    setCopiedId(guestId);
    setTimeout(() => setCopiedId(null), 2000);
    trackEvent({ eventName: "app/doc_list/share_case/copy_link" });
  };

  const handleRevoke = (accessId: string) => {
    revokeAccess({
      organizationId,
      projectId: caseId,
      caseId,
      accessId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg px-0">
        <DialogHeader className="px-6">
          <DialogTitle>
            <FormattedMessage
              id="share.title"
              defaultMessage="Share Current Version"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="share.description"
              defaultMessage="Generate secure shareable view-only link for {versionName}. Email is used only for authorization; the link will not be sent automatically."
              values={{
                versionName: <strong className="font-bold">{caseName}</strong>,
              }}
            />
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {guests && guests.length > 0 && (
            <div className="flex flex-col gap-2 border-b border-[#E4E4E7]">
              <Label className="text-sm font-medium text-zinc-700 px-6">
                <FormattedMessage
                  id="share.secureLinks"
                  defaultMessage="Secure Links"
                />
              </Label>
              <div className="max-h-[412px] overflow-y-auto px-6">
                {guests.map((guest) => (
                  <div
                    key={guest.id}
                    className="flex items-center justify-between px-0 pb-[12px]"
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="mb-[4px] truncate text-[#18181B] text-sm">
                        {guest.shareUrl || "No link available"}
                      </span>
                      <span className="text-sm text-zinc-500 truncate">
                        {guest.email?.replace(/\+guest@/, "@")}
                      </span>
                    </div>
                    <div className="flex items-center ml-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-500"
                        onClick={() =>
                          handleCopyLink(guest.id!, guest.shareUrl!)
                        }
                      >
                        {copiedId === guest.id ? (
                          <CheckIcon className="h-4 w-4" />
                        ) : (
                          <CopyIcon className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleRevoke(guest.id!)}
                        disabled={isRevoking}
                      >
                        <Trash2Icon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2 items-end px-6">
            <div className="flex flex-col gap-2 flex-1">
              <Label htmlFor="share-email">
                <FormattedMessage
                  id="share.emailLabel"
                  defaultMessage="Email address"
                />
              </Label>
              <Input
                id="share-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                placeholder={intl.formatMessage({
                  id: "share.emailPlaceholder",
                  defaultMessage: "guest@example.com",
                })}
                className={error ? "border-red-500" : ""}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit();
                  }
                }}
              />
            </div>
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <FormattedMessage
                id={isPending ? "share.generatingLink" : "share.generateLink"}
                defaultMessage="Generate Link"
              />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
